import { createReducer } from '@reduxjs/toolkit';

import { queueUploadsAction, setUploadAbortedAction } from '@air/redux-uploader';

import {
  addUploadingClipToContentCollectionAction,
  deleteContentCollectionClipsAction,
  setApplyAllAction,
  setAttemptedSubmissionAction,
  setAutoSubmitStatusAction,
  setCustomFieldDateValueAction,
  setCustomFieldPlainTextValueAction,
  setCustomFieldsAction,
  setCustomFieldValueAction,
  setCustomFieldValuesAction,
  setFormAction,
  setFormSubmissionRequiredAction,
  setSessionIdAction,
  setSubmittedContentCollectionClipsAction,
  setSubmitterNameAction,
  setTouchedAction,
  updateContentCollectionClipCustomFieldAction,
  updateContentCollectionClipsAction,
  updateContentCollectionCustomFieldsInAllClipsAction,
} from './actions';
import { type ContentCollectionState, ContentCollectionView } from './types';

export const initialState: ContentCollectionState = {
  applyAll: false,
  attemptedSubmission: false,
  clips: {},
  customFields: [],
  form: undefined,
  submittedClips: [],
  touched: false,
  view: ContentCollectionView.Onboarding,
};

export const reducer = createReducer(initialState, (builder) => {
  builder.addCase(setApplyAllAction, (state, { payload: { applyAll } }) => {
    state.applyAll = applyAll;
  });

  builder.addCase(setCustomFieldsAction, (state, { payload: { customFields } }) => {
    state.customFields = customFields;
  });

  builder.addCase(setCustomFieldPlainTextValueAction, (state, { payload: { customField } }) => {
    const customFieldIndex = state.customFields.findIndex(({ id }) => id === customField.id);

    if (customFieldIndex !== -1) {
      state.customFields[customFieldIndex] = customField;
    } else {
      state.customFields.push(customField);
    }
  });

  builder.addCase(setCustomFieldDateValueAction, (state, { payload: { customField } }) => {
    const customFieldIndex = state.customFields.findIndex(({ id }) => id === customField.id);

    if (customFieldIndex !== -1) {
      state.customFields[customFieldIndex] = customField;
    } else {
      state.customFields.push(customField);
    }
  });

  builder.addCase(setCustomFieldValuesAction, (state, { payload: { customField } }) => {
    const customFieldIndex = state.customFields.findIndex(({ id }) => id === customField.id);

    if (customFieldIndex !== -1) {
      state.customFields[customFieldIndex] = customField;
    } else {
      state.customFields.push(customField);
    }
  });

  builder.addCase(setCustomFieldValueAction, (state, { payload: { customField } }) => {
    const customFieldIndex = state.customFields.findIndex(({ id }) => id === customField.id);

    if (customFieldIndex !== -1) {
      state.customFields[customFieldIndex] = customField;
    } else {
      state.customFields.push(customField);
    }
  });

  builder.addCase(setTouchedAction, (state, { payload: { touched } }) => {
    state.touched = touched;
  });

  builder.addCase(queueUploadsAction, (state) => {
    if (!state.form) return;
    state.view = state.form.autoSubmit ? ContentCollectionView.AutoSubmit : ContentCollectionView.Manage;
  });

  builder.addCase(setFormAction, (state, { payload: { form } }) => {
    state.form = form;
  });

  builder.addCase(setAttemptedSubmissionAction, (state, { payload: { attemptedSubmission } }) => {
    state.attemptedSubmission = attemptedSubmission;
  });

  builder.addCase(setSessionIdAction, (state, { payload: { sessionId } }) => {
    state.sessionId = sessionId;
  });

  builder.addCase(addUploadingClipToContentCollectionAction, (state, { payload: { clip } }) => {
    state.clips = {
      ...state.clips,
      [clip.id]: {
        ...clip,
        autoSubmit: state.form?.autoSubmit ? { status: 'pending' } : undefined,
        // after a clip is uploaded and set inside this reducer, we want to set it's title field equal to importedName
        // this is because board collection always requires a title to be present
        title: clip.importedName,
      },
    };
  });

  builder.addCase(deleteContentCollectionClipsAction, (state, { payload: { clipIds } }) => {
    clipIds.forEach((id) => {
      delete state.clips[id];
    });

    if (Object.keys(state.clips).length === 0) {
      state.view = ContentCollectionView.Onboarding;
    }
  });

  builder.addCase(updateContentCollectionClipsAction, (state, { payload: { clips } }) => {
    clips.forEach((clip) => {
      state.clips[clip.id] = {
        ...(state.clips[clip.id] || {}),
        ...clip,
      };
    });
  });

  builder.addCase(updateContentCollectionClipCustomFieldAction, (state, { payload: { clipId, customField } }) => {
    const clip = state.clips[clipId];
    if (!clip) return;

    clip.customFields = [
      ...(clip.customFields?.filter((cf) => cf.id !== customField.id) || []),
      {
        ...customField,
        assetId: clip.assetId,
      },
    ];
  });

  builder.addCase(updateContentCollectionCustomFieldsInAllClipsAction, (state, { payload: { customField } }) => {
    Object.values(state.clips).forEach((clip) => {
      if (clip.customFields?.length) {
        if (clip.customFields.some((cf) => cf.id === customField.id)) {
          clip.customFields = clip.customFields.map((cf) =>
            cf.id === customField.id
              ? {
                  ...cf,
                  ...customField,
                }
              : cf,
          );
        } else {
          clip.customFields.push({
            ...customField,
            assetId: clip.assetId,
          });
        }
      } else {
        clip.customFields = [
          {
            ...customField,
            assetId: clip.assetId,
          },
        ];
      }
    });
  });

  builder.addCase(setUploadAbortedAction, (state, { payload: { uploadId } }) => {
    state.clips = Object.fromEntries(Object.entries(state.clips).filter(([_, clip]) => clip.uploadId !== uploadId));
  });

  builder.addCase(setSubmittedContentCollectionClipsAction, (state, { payload: { clips } }) => {
    state.submittedClips = clips.map((clip) => ({
      ...clip,
      upload: {
        ...clip.upload,
        // There's a type issue with WriteableDraft<Upload> here unless we set `xhr` to `undefined`
        xhr: undefined,
      },
    }));
    state.clips = {};
    state.view = ContentCollectionView.Completed;
  });

  builder.addCase(setSubmitterNameAction, (state, { payload: { name } }) => {
    state.submitterName = name;
  });

  builder.addCase(setFormSubmissionRequiredAction, (state, { payload: { formSubmissionRequired } }) => {
    state.formSubmissionRequired = formSubmissionRequired;
  });

  builder.addCase(setAutoSubmitStatusAction, (state, { payload: { clipId, ...autoSubmit } }) => {
    if (!state.clips[clipId]) return;
    state.clips[clipId].autoSubmit = autoSubmit;
  });
});
